// ─── Bulk import contacts from CSV (Phase 3 follow-up) ───────────────────────
//
// Admin uploads CSV file. We parse + validate + show a preview table:
//   - ✓ valid: will be created (or dedup-matched to existing)
//   - ⚠ invalid: shown with error reason, skipped on submit
// Submit calls createContact() for each valid row. Source='csv-import',
// needsReview=false (CSV is admin-curated, trusted).

import React, { useState } from 'react';
import { parseCsv, CONTACT_CSV_TEMPLATE } from '../lib/csv';
import { isValidEmail } from '../lib/email';
import { isValidPhone } from '../lib/phone';
import { createContact } from '../services/contactsService';
import type { ContactCreateInput } from '../services/contactsService';
import type { UserType } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: (summary: ImportSummary) => void;
}

type RowStatus = 'pending' | 'valid' | 'invalid' | 'created' | 'duplicate' | 'error';

interface CsvRow {
  lineNumber: number;
  raw: Record<string, string>;
  status: RowStatus;
  error?: string;
  contactId?: string;
}

export interface ImportSummary {
  total: number;
  created: number;
  duplicate: number;
  errors: number;
}

const REQUIRED_HEADERS = ['name', 'email'];
const VALID_USER_TYPES: UserType[] = ['employee', 'external_worker', 'vendor', 'guest'];

const ImportContactsCsvModal: React.FC<Props> = ({ open, onClose, onImported }) => {
  const [text, setText]         = useState('');
  const [rows, setRows]         = useState<CsvRow[]>([]);
  const [missingHeaders, setMissingHeaders] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState<string | null>(null);

  if (!open) return null;

  function loadText(raw: string) {
    setText(raw);
    setError(null);
    const parsed = parseCsv(raw);
    const missing = REQUIRED_HEADERS.filter((h) => !parsed.headers.includes(h));
    setMissingHeaders(missing);
    if (missing.length > 0) {
      setRows([]);
      return;
    }
    // Validate each row
    const validated: CsvRow[] = parsed.rows.map((r, i) => validateRow(r, parsed.lineNumbers[i]));
    setRows(validated);
  }

  async function handleFileChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    if (!file) return;
    const raw = await file.text();
    loadText(raw);
  }

  function downloadTemplate() {
    const blob = new Blob([CONTACT_CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleImport() {
    setSubmitting(true);
    setError(null);
    const updated = [...rows];
    let created = 0;
    let duplicate = 0;
    let errors = 0;

    for (let i = 0; i < updated.length; i++) {
      const row = updated[i];
      if (row.status !== 'valid') continue;
      try {
        const input: ContactCreateInput = {
          email: row.raw.email,
          displayName: row.raw.name,
          phone: row.raw.phone || undefined,
          user_type: (VALID_USER_TYPES.includes(row.raw.user_type as UserType)
            ? row.raw.user_type
            : undefined) as UserType | undefined,
          department: row.raw.department || undefined,
          jobTitle: row.raw.jobTitle || undefined,
          employee_id: row.raw.employee_id || undefined,
          site: row.raw.site || undefined,
          source: 'csv-import',
          needsReview: false,
        };
        const result = await createContact(input);
        if (result.wasCreated) {
          updated[i] = { ...row, status: 'created', contactId: result.contactId };
          created++;
        } else {
          updated[i] = { ...row, status: 'duplicate', contactId: result.contactId };
          duplicate++;
        }
      } catch (e: any) {
        updated[i] = { ...row, status: 'error', error: e?.message ?? String(e) };
        errors++;
      }
      // Update progress every row so user sees the work
      setRows([...updated]);
    }

    const total = updated.filter((r) => ['valid', 'created', 'duplicate', 'error'].includes(r.status)).length;
    const summary: ImportSummary = { total, created, duplicate, errors };
    setSubmitting(false);
    onImported(summary);
  }

  const validCount    = rows.filter((r) => r.status === 'valid').length;
  const invalidCount  = rows.filter((r) => r.status === 'invalid').length;
  const createdCount  = rows.filter((r) => r.status === 'created').length;
  const dupCount      = rows.filter((r) => r.status === 'duplicate').length;
  const errorCount    = rows.filter((r) => r.status === 'error').length;
  const allDone       = rows.length > 0 && rows.every((r) => r.status !== 'valid');

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={submitting ? undefined : onClose} />
      <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto pointer-events-none">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl pointer-events-auto my-8"
             style={{ fontFamily: 'Inter, sans-serif' }}>
          {/* Header */}
          <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-stone-900">Import Contacts from CSV</h2>
              <p className="text-xs text-stone-500 mt-0.5">
                Required columns: <code>name</code>, <code>email</code>. Optional: phone, user_type, department, jobTitle, employee_id, site, startDate.
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={submitting}
              className="text-stone-400 hover:text-stone-700 disabled:opacity-50"
            >
              <span className="material-symbols-rounded" style={{ fontSize: 20 }}>close</span>
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-4">
            {/* Step 1 — upload */}
            <div className="flex flex-wrap items-center gap-2">
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50">
                <span className="material-symbols-rounded text-stone-500" style={{ fontSize: 18 }}>upload_file</span>
                Choose CSV file
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={submitting}
                />
              </label>
              <button
                onClick={downloadTemplate}
                disabled={submitting}
                className="text-xs text-blue-700 hover:underline"
              >
                Download template
              </button>
              <span className="text-xs text-stone-400 flex-1 text-right">
                {rows.length > 0 && `${rows.length} row(s) parsed`}
              </span>
            </div>

            {/* Errors */}
            {missingHeaders.length > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                Missing required column(s): <code>{missingHeaders.join(', ')}</code>
              </div>
            )}

            {/* Preview table */}
            {rows.length > 0 && (
              <>
                <div className="flex items-center gap-3 text-xs flex-wrap">
                  <span className="text-emerald-700">✓ {validCount} valid</span>
                  {invalidCount > 0 && <span className="text-red-700">✗ {invalidCount} invalid</span>}
                  {createdCount > 0 && <span className="text-emerald-700 font-semibold">+ {createdCount} created</span>}
                  {dupCount > 0 && <span className="text-amber-700">⤺ {dupCount} duplicate</span>}
                  {errorCount > 0 && <span className="text-red-700">⚠ {errorCount} error</span>}
                </div>

                <div className="max-h-72 overflow-y-auto border border-stone-200 rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-stone-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold text-stone-600">#</th>
                        <th className="text-left px-3 py-2 font-semibold text-stone-600">Status</th>
                        <th className="text-left px-3 py-2 font-semibold text-stone-600">Name</th>
                        <th className="text-left px-3 py-2 font-semibold text-stone-600">Email</th>
                        <th className="text-left px-3 py-2 font-semibold text-stone-600">Phone</th>
                        <th className="text-left px-3 py-2 font-semibold text-stone-600">Note</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {rows.map((r, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 text-stone-400">{r.lineNumber}</td>
                          <td className="px-3 py-2">
                            <StatusPill status={r.status} />
                          </td>
                          <td className="px-3 py-2 text-stone-700">{r.raw.name}</td>
                          <td className="px-3 py-2 text-stone-700">{r.raw.email}</td>
                          <td className="px-3 py-2 text-stone-500">{r.raw.phone || '—'}</td>
                          <td className="px-3 py-2 text-stone-500">{r.error ?? (r.contactId ? r.contactId.slice(0, 12) + '…' : '')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-stone-100 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 rounded-lg border border-stone-200"
            >
              {allDone ? 'Close' : 'Cancel'}
            </button>
            {!allDone && (
              <button
                onClick={handleImport}
                disabled={submitting || validCount === 0}
                className="px-5 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50"
                style={{ background: '#011132' }}
              >
                {submitting ? `Importing ${createdCount + dupCount + errorCount}/${validCount}…` : `Import ${validCount} contact(s)`}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Hidden textarea for paste (alternative to file picker) — kept simple, just text source via file */}
      <textarea value={text} readOnly style={{ display: 'none' }} aria-hidden />
    </>
  );
};

const StatusPill: React.FC<{ status: RowStatus }> = ({ status }) => {
  const cls = {
    pending:    'bg-stone-100 text-stone-600',
    valid:      'bg-emerald-50 text-emerald-700',
    invalid:    'bg-red-50 text-red-700',
    created:    'bg-emerald-100 text-emerald-800 font-semibold',
    duplicate:  'bg-amber-50 text-amber-700',
    error:      'bg-red-100 text-red-800',
  }[status];
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded ${cls}`}>{status}</span>
  );
};

function validateRow(raw: Record<string, string>, lineNumber: number): CsvRow {
  if (!raw.name) {
    return { lineNumber, raw, status: 'invalid', error: 'name required' };
  }
  if (!raw.email) {
    return { lineNumber, raw, status: 'invalid', error: 'email required' };
  }
  if (!isValidEmail(raw.email)) {
    return { lineNumber, raw, status: 'invalid', error: `invalid email: ${raw.email}` };
  }
  if (raw.phone && !isValidPhone(raw.phone)) {
    return { lineNumber, raw, status: 'invalid', error: `invalid phone: ${raw.phone}` };
  }
  if (raw.user_type && !VALID_USER_TYPES.includes(raw.user_type as UserType)) {
    return { lineNumber, raw, status: 'invalid', error: `invalid user_type: ${raw.user_type}` };
  }
  return { lineNumber, raw, status: 'valid' };
}

export default ImportContactsCsvModal;
